import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronDown } from 'lucide-react';

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  title?: string;
}

const ColorPicker: React.FC<ColorPickerProps> = ({ value, onChange, title }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownShiftX, setDropdownShiftX] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownPanelRef = useRef<HTMLDivElement>(null);

  // Animation variants for dropdown and swatches
  const dropdownVariants = {
    hidden: { opacity: 0, y: -4, scale: 0.99 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.12, ease: [0.2, 0.8, 0.2, 1], staggerChildren: 0.004, delayChildren: 0.01 },
    },
    exit: {
      opacity: 0,
      y: -3,
      scale: 0.99,
      transition: { duration: 0.12, ease: [0.2, 0.8, 0.2, 1], staggerChildren: 0.003, staggerDirection: -1 },
    },
  } as const;

  const swatchVariants = {
    hidden: { opacity: 0, scale: 0.94 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.08 } },
    exit: { opacity: 0, scale: 0.94, transition: { duration: 0.08 } },
  } as const;

  // Clean, organized color palette matching the design
  const colorPalette = [
    // Row 1 - Grays (10)
    ['#000000', '#333333', '#4D4D4D', '#666666', '#808080', '#B3B3B3', '#D9D9D9', '#EDEDED', '#F5F5F5', '#FFFFFF'],

    // Row 2 - Bright hues (10)
    ['#FF0000', '#FF6A00', '#FFB300', '#FFFF00', '#7CFF00', '#00FF66', '#00E5FF', '#007BFF', '#7A00FF', '#FF00FF'],

    // Row 3 - Light pastels (10)
    ['#FFC0C0', '#FFD1A6', '#FFE8A6', '#FFFFC0', '#D9FFC0', '#C0FFE5', '#C0F6FF', '#C0D9FF', '#D6C0FF', '#FFC0F6'],

    // Row 4 - Soft tones (10)
    ['#FF9999', '#FFC285', '#FFE085', '#F9F99A', '#B6FFA0', '#9EF5CC', '#9EEAFF', '#9EC3FF', '#C1A0FF', '#FFA0E8'],

    // Row 5 - Medium tones (10)
    ['#FF6666', '#FFA04D', '#FFCC4D', '#F2F26B', '#8CF067', '#67E6B2', '#67D7F7', '#67A0F2', '#A575FF', '#FF75D1'],

    // Row 6 - Rich tones (10)
    ['#FF3333', '#FF851A', '#FFB01A', '#E0E04E', '#6ADC43', '#43CFA5', '#43C2EB', '#4382E6', '#8B57F5', '#FF57B8'],

    // Row 7 - Dark tones (10)
    ['#CC0000', '#CC6D00', '#CC8E00', '#BFBF3A', '#50B736', '#36A78B', '#3699C9', '#366AB8', '#6C3DE5', '#CC3DA3'],

    // Row 8 - Deep tones (10)
    ['#990000', '#994D00', '#996600', '#8C8C2E', '#3A8F29', '#2A7F6C', '#2A78A3', '#2A4F8A', '#4B2DB8', '#992D82']
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      const panel = dropdownPanelRef.current;
      if (!panel) return;

      const rect = panel.getBoundingClientRect();
      const viewportPadding = 8;
      let shift = 0;

      // Nudge left when panel exceeds the right viewport edge.
      if (rect.right > window.innerWidth - viewportPadding) {
        shift -= rect.right - (window.innerWidth - viewportPadding);
      }

      // If that nudge over-corrects, keep a small left padding.
      if (rect.left + shift < viewportPadding) {
        shift += viewportPadding - (rect.left + shift);
      }

      setDropdownShiftX(Math.round(shift));
    };

    const raf = requestAnimationFrame(updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updatePosition);
      setDropdownShiftX(0);
    };
  }, [isOpen]);

  const handleColorSelect = (color: string) => {
    onChange(color);
    setIsOpen(false);
  };

  return (
    <div className="relative color-picker" ref={dropdownRef}>
      {/* Color Preview Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="color-preview w-8 h-8 rounded-full border-2 transition-colors focus:outline-none shadow-sm"
        style={{ 
          // Pass color via CSS var so we can apply !important in stylesheet
          ['--preview-color' as any]: value,
          backgroundColor: value,
          border: value === '#FFFFFF' || value === '#ffffff' || value.toLowerCase() === '#ffffff'
            ? '2px solid #9ca3af'
            : '2px solid #d1d5db',
          boxShadow: value === '#FFFFFF' || value === '#ffffff' || value.toLowerCase() === '#ffffff'
            ? 'inset 0 0 0 1px rgba(0,0,0,0.2), 0 1px 3px 0 rgba(0, 0, 0, 0.1)'
            : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
        }}
        title={title}
      />

      {/* Color Palette Dropdown */}
      <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={dropdownPanelRef}
          variants={dropdownVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="color-picker-dropdown absolute top-full left-0 mt-2 p-4 bg-white border border-gray-200 rounded-xl shadow-xl z-50 w-auto min-w-[320px] max-w-[calc(100vw-1rem)] overflow-x-auto"
          style={{ marginLeft: `${dropdownShiftX}px` }}
        >
          {/* Color Grid */}
          <div className="space-y-2">
            {colorPalette.map((row, rowIndex) => (
              <div key={rowIndex} className="grid grid-cols-10 gap-2">
                {row.map((color) => (
                  <motion.button
                    key={color}
                    type="button"
                    onClick={() => handleColorSelect(color)}
                    variants={swatchVariants}
                    className={`color-swatch w-6 h-6 rounded-full border-2 hover:scale-110 transition-all duration-150 cursor-pointer ${
                      value.toLowerCase() === color.toLowerCase() 
                        ? 'ring-2 ring-blue-500 ring-offset-2' 
                        : ''
                    }`}
                    style={{ 
                      ['--swatch-color' as any]: color,
                      backgroundColor: color,
                      border: color === '#FFFFFF' || color === '#F3F3F3' || color === '#EFEFEF' 
                        ? '2px solid #d1d5db' 
                        : '2px solid transparent'
                    }}
                    title={color}
                  />
                ))}
              </div>
            ))}
          </div>
          
          {/* Custom section */}
          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="text-xs font-medium text-gray-500 mb-3 uppercase tracking-wide">Custom</div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <input
                  type="color"
                  value={value}
                  onChange={(e) => handleColorSelect(e.target.value)}
                  className="w-8 h-8 rounded-full border-2 border-gray-200 cursor-pointer overflow-hidden"
                  title="Custom Color Picker"
                />
                <div className="absolute inset-0 w-8 h-8 rounded-full border-2 border-gray-200 pointer-events-none"></div>
              </div>
              <input
                type="text"
                value={value}
                onChange={(e) => handleColorSelect(e.target.value)}
                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="#000000"
                maxLength={7}
              />
            </div>
          </div>
        </motion.div>
      )}
      </AnimatePresence>
    </div>
  );
};

export default ColorPicker;
